import { Component, DestroyRef, inject, signal, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import {
  DropdownData,
  DynamicRouteHandlingService,
  rxResourceFixed,
  InputFieldComponent,
  GenericDropdownComponent,
  NgbModalService,
  LookupsService
} from '@zayan-erp/shared';
import { ProjectsService, OUnitCodeService, LotsService } from '@zayan-erp/home-building';
import { SchedulerService } from '@zayan-erp/scheduler';
import { MoveFolderComponent } from '@zayan-erp/file-manager';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, of, tap, switchMap, startWith, catchError, merge, BehaviorSubject } from 'rxjs';

@Component({
  selector: 'app-lot-form-modal',
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule, InputFieldComponent, GenericDropdownComponent, CommonModule],
  templateUrl: './lot-form-modal.component.html'
})
export class LotFormModalComponent {
  private fb = inject(FormBuilder);
  public modalService = inject(NgbModal);
  private ngbSharedModalService = inject(NgbModalService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private projectsService = inject(ProjectsService);
  private ounitCodeService = inject(OUnitCodeService);
  private schedulerService = inject(SchedulerService);
  private lotsService = inject(LotsService);
  private dynamicRouteHandling = inject(DynamicRouteHandlingService);
  private lookupsService = inject(LookupsService);

  // ─── State Management ───────────────────────────────────────────────────────
  isEdit = false;
  lotId = signal<string | null>(this.route.snapshot.paramMap.get('id'));
  lotFolderName = signal<string>('');
  /** Set to true by callers that host this component in an NgbModal (e.g. Build Spec & Model) — swaps
   *  navigation for output emits. Set via `modalRef.componentInstance.embedded.set(true)` after opening. */
  embedded = signal<boolean>(false);

  saved = output<any>();
  cancelled = output<void>();

  // ─── Form Definition ────────────────────────────────────────────────────────
  lotForm: FormGroup = this.fb.group({
    lotNo: ['', Validators.required],
    operatingUnitId: [null],
    projectCode: ['', Validators.required],
    warRefNo: [null],
    modelName: [''],
    planNo: [null],
    permitNo: [null],
    legalUnit: [null],
    mrktgUnitNo: [null],
    scheduleId: [null],
    jobTypeId: [null],
    lotTypeId: [null],
    swing: [null],
    softCostLc: [null],
    postAllow: [null],
    lotPremiumAmount: [null],
    descr: [null],
    sortKey: [null],
    lotFolderId: [null],
    floorplanFileLink: [null],
    buyerDocumentLink: [null],
    address: this.fb.group({
      cityId: [null],
      streetNo: [null],
      street: [null],
      street2: [null],
      suite: [null],
      postal: [null, [Validators.pattern('[A-Za-z0-9 ]*')]]
    })
  });

  // ─── Static Dropdown Signals ────────────────────────────────────────────────
  operatingUnits = toSignal(
    this.ounitCodeService.readOUnitCodes().pipe(map(res => this.mapToDropdown(res, 'id', 'oUnitCode', 'name'))),
    { initialValue: [] }
  );

  schedules = toSignal(
    this.schedulerService.readSchedule().pipe(map(res => this.mapToDropdown(res, 'id', 'name', 'code'))),
    { initialValue: [] }
  );

  jobTypes = toSignal(
    this.lotsService.readJobTypes().pipe(map(res => this.mapToDropdown(res, 'id', 'code', 'abbreviation'))),
    { initialValue: [] }
  );

  lotTypes = toSignal(
    this.lotsService.readLotTypes().pipe(
      map(res => this.mapToDropdown(res, 'id', 'name')),

      tap(mappedTypes => {
        if (!this.isEdit && mappedTypes.length > 0) {
          this.lotForm.patchValue({ lotTypeId: mappedTypes[1].id });
        }
      })
    ),
    { initialValue: [] as DropdownData[] }
  );

  // Swing is L or R only; the API rejects anything else.
  readonly swingOptions: DropdownData[] = [
    { id: 'L', firstLabel: 'L' },
    { id: 'R', firstLabel: 'R' }
  ];

  cities = toSignal(this.projectsService.readCities().pipe(map(res => this.mapToDropdown(res, 'id', 'cityName'))), {
    initialValue: []
  });

  manualProjectOptionsRefresh = new BehaviorSubject<void>(undefined);

  projectsOptions = toSignal(
    merge(this.lotForm.get('operatingUnitId')!.valueChanges, this.manualProjectOptionsRefresh).pipe(
      map(() => this.lotForm.get('operatingUnitId')?.value),
      startWith(this.lotForm.get('operatingUnitId')?.value),
      tap(() => {
        if (this.lotForm.get('operatingUnitId')?.dirty) {
          this.lotForm.get('projectCode')?.setValue(null);
        }
      }),
      switchMap(ouId => {
        if (!ouId) {
          const currentProjectCode = this.lotForm.get('projectCode')?.value;
          if (this.isEdit && currentProjectCode && !this.lotForm.get('operatingUnitId')?.dirty) {
            return of([{ id: currentProjectCode, firstLabel: currentProjectCode }] as DropdownData[]);
          }
          return of([] as DropdownData[]);
        }

        return this.lookupsService.readProjectsByOUnitCodeId(ouId).pipe(
          map((res: any) => {
            const projectsArray = Array.isArray(res) ? res : res?.data || [];

            const mapped = projectsArray.map((item: any) => ({
              id: item.firstLabel,
              firstLabel: item.firstLabel,
              secondLabel: item.secondLabel
            })) as DropdownData[];

            const currentProjectCode = this.lotForm.get('projectCode')?.value;
            if (this.isEdit && currentProjectCode && !this.lotForm.get('operatingUnitId')?.dirty) {
              if (!mapped.some(m => m.id === currentProjectCode)) {
                mapped.push({ id: currentProjectCode, firstLabel: currentProjectCode });
              }
            }

            return mapped;
          }),
          catchError(err => {
            console.error('Failed to fetch projects', err);
            return of([] as DropdownData[]);
          })
        );
      })
    ),
    { initialValue: [] as DropdownData[] }
  );

  recordToEditRxResource = rxResourceFixed<any, { id: string | null }>({
    params: () => ({ id: this.lotId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params.id) return of(null);
      return this.lotsService.getLotById(params.id).pipe(
        tap((lot: any) => {
          if (lot) {
            this.isEdit = true;
            this.lotForm.patchValue(lot);
            this.lotFolderName.set(lot.lotFolderName || '');
            this.disableFieldsOnEdit();
            this.manualProjectOptionsRefresh.next();
          }
        })
      );
    }
  });

  onCancel(): void {
    if (this.embedded()) {
      this.cancelled.emit();
      return;
    }
    this.router.navigate(['/home-building/lots'], { queryParamsHandling: 'preserve' });
  }

  openFolderModal(): void {
    const modalRef = this.modalService.open(MoveFolderComponent, this.ngbSharedModalService.centerModal);
    modalRef.result.then(
      result => {
        if (result) {
          this.lotForm.patchValue({ lotFolderId: result.id });
          this.lotFolderName.set(result.name);
        }
      },
      () => {}
    );
  }

  disableFieldsOnEdit(): void {
    this.lotForm.get('lotNo')?.disable();
    this.lotForm.get('projectCode')?.disable();
    this.lotForm.get('operatingUnitId')?.disable();
  }

  onSubmit(): void {
    if (this.lotForm.invalid) {
      this.lotForm.markAllAsTouched();
      return;
    }

    const { operatingUnitId, lotFolderId, ...lotData } = this.lotForm.getRawValue();

    const request$ = this.isEdit
      ? this.lotsService.updateLot({ ...lotData, id: this.lotId() })
      : this.lotsService.createLot(lotData);

    request$.subscribe({
      next: (res: any) => {
        this.afterFormSubmitSuccess(res);
      }
    });
  }

  afterFormSubmitSuccess(res: any): void {
    if (this.embedded()) {
      this.saved.emit(res);
      return;
    }

    this.dynamicRouteHandling.reload.next(!this.dynamicRouteHandling.reload.value);
    const newId = res?.id || res?.data?.id;
    if (!this.isEdit && newId) {
      this.router.navigate(['/home-building/lots', newId], {
        queryParamsHandling: 'preserve'
      });
    }
  }

  private mapToDropdown<T>(data: T[], idKey: keyof T, label1Key: keyof T, label2Key?: keyof T): DropdownData[] {
    if (!Array.isArray(data)) return [];

    return data.map(item => ({
      id: item[idKey] as any,
      firstLabel: String(item[label1Key]),
      secondLabel: label2Key ? String(item[label2Key]) : undefined
    }));
  }
}
